import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { BsChevronLeft, BsChevronRight } from "react-icons/bs";
import ArticleCarouselCard from "./ArticleCarouselCard";
import { getFeaturedArticles } from "../../data/articles";
import { SECTION_TITLES } from "../../config/constants";
import { ArticleCardSkeleton } from "../common/LoadingSkeleton";

const SLIDES_PER_PAGE = 3;

function FeaturedArticlesCarousel() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const trackRef = useRef(null);
  const activeIndexRef = useRef(0);
  const isNavigatingRef = useRef(false);
  const navTimerRef = useRef(null);

  const pageCount = Math.ceil(articles.length / SLIDES_PER_PAGE);
  const activePage = Math.floor(activeIndex / SLIDES_PER_PAGE);

  useEffect(() => {
    setArticles(getFeaturedArticles());
    setLoading(false);
  }, []);

  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  const getSlideScrollLeft = useCallback((slide, track) => {
    return (
      slide.getBoundingClientRect().left -
      track.getBoundingClientRect().left +
      track.scrollLeft
    );
  }, []);

  const getCurrentIndex = useCallback(() => {
    const track = trackRef.current;
    if (!track) return 0;

    const slides = track.querySelectorAll(".carousel-slide");
    if (!slides.length) return 0;

    let closestIndex = 0;
    let closestDistance = Infinity;

    slides.forEach((slide, index) => {
      const distance = Math.abs(
        getSlideScrollLeft(slide, track) - track.scrollLeft
      );
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  }, [getSlideScrollLeft]);

  const finishNavigation = useCallback(() => {
    isNavigatingRef.current = false;
    const index = getCurrentIndex();
    setActiveIndex(index);
    activeIndexRef.current = index;
  }, [getCurrentIndex]);

  const scrollToIndex = useCallback(
    (index) => {
      const track = trackRef.current;
      if (!track || !articles.length) return;

      const slides = track.querySelectorAll(".carousel-slide");
      if (!slides.length) return;

      const clampedIndex = Math.min(
        Math.max(index, 0),
        slides.length - 1
      );
      const slide = slides[clampedIndex];
      if (!slide) return;

      isNavigatingRef.current = true;
      setActiveIndex(clampedIndex);
      activeIndexRef.current = clampedIndex;

      track.scrollTo({
        left: getSlideScrollLeft(slide, track),
        behavior: "smooth",
      });

      clearTimeout(navTimerRef.current);
      navTimerRef.current = setTimeout(finishNavigation, 450);

      const handleScrollEnd = () => {
        clearTimeout(navTimerRef.current);
        finishNavigation();
      };

      if ("onscrollend" in track) {
        track.addEventListener("scrollend", handleScrollEnd, { once: true });
      }
    },
    [articles.length, getSlideScrollLeft, finishNavigation]
  );

  const scrollToPage = (pageIndex) => {
    scrollToIndex(pageIndex * SLIDES_PER_PAGE);
  };

  const goToNext = () => {
    scrollToIndex(activeIndexRef.current + 1);
  };

  const goToPrev = () => {
    scrollToIndex(activeIndexRef.current - 1);
  };

  useEffect(() => {
    const track = trackRef.current;
    if (!track || !articles.length) return;

    const handleScroll = () => {
      if (isNavigatingRef.current) return;
      const index = getCurrentIndex();
      setActiveIndex(index);
      activeIndexRef.current = index;
    };

    handleScroll();
    track.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);

    return () => {
      track.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
      clearTimeout(navTimerRef.current);
    };
  }, [articles, getCurrentIndex]);

  if (!loading && articles.length === 0) return null;

  const titleParts = SECTION_TITLES.articles.featured.split(" ");
  const titleLead = titleParts.slice(0, -1).join(" ");
  const titleAccent = titleParts[titleParts.length - 1];
  const showPagination = !loading && articles.length > 1;

  return (
    <section className="featured-articles-section">
      <h2 className="about-block-heading">
        {titleLead} <strong className="purple">{titleAccent}</strong>
      </h2>
      <p className="featured-articles-subtitle">
        {SECTION_TITLES.articles.featuredSubtitle}
      </p>

      <div className="featured-articles-carousel">
        {showPagination && (
          <>
            <button
              type="button"
              className="carousel-nav carousel-nav-prev"
              onClick={goToPrev}
              disabled={activeIndex === 0}
              aria-label="Previous article"
            >
              <BsChevronLeft />
            </button>
            <button
              type="button"
              className="carousel-nav carousel-nav-next"
              onClick={goToNext}
              disabled={activeIndex >= articles.length - 1}
              aria-label="Next article"
            >
              <BsChevronRight />
            </button>
          </>
        )}

        <div className="carousel-track" ref={trackRef}>
          {loading
            ? [...Array(3)].map((_, index) => (
                <div key={index} className="carousel-slide">
                  <ArticleCardSkeleton />
                </div>
              ))
            : articles.map((article, index) => (
                <div key={`${article.id}-${index}`} className="carousel-slide">
                  <ArticleCarouselCard article={article} />
                </div>
              ))}
        </div>

        {showPagination && pageCount > 1 && (
          <div
            className="carousel-dots"
            role="tablist"
            aria-label="Featured article pages"
          >
            {Array.from({ length: pageCount }, (_, pageIndex) => (
              <button
                key={pageIndex}
                type="button"
                className={`carousel-dot${
                  pageIndex === activePage ? " active" : ""
                }`}
                onClick={() => scrollToPage(pageIndex)}
                aria-label={`Go to page ${pageIndex + 1}`}
                aria-selected={pageIndex === activePage}
                role="tab"
              />
            ))}
          </div>
        )}
      </div>

      <div className="featured-articles-footer">
        <Link to="/articles" className="featured-articles-view-all">
          View all articles →
        </Link>
      </div>
    </section>
  );
}

export default FeaturedArticlesCarousel;
